/**
 * <WcRegisterBanner mode="tournament|immersion|dream-team|ctf" platform? />
 *
 * Pre-launch "Register Now" interest banner inside each WC 2026 mode page.
 * iter 96 — the player MUST pick a platform (mobile / console) before they
 * register, since each platform is a separate league. Posts {mode, platform}
 * to /api/wc/register (idempotent). After registering, flips to a "Thanks for
 * registering" confirmation that shows the chosen platform.
 *
 * If a `platform` prop is supplied (e.g. CtfHub already has a platform
 * switcher), the chooser is skipped and a single Register button is shown.
 *
 * Doesn't unlock any gameplay — purely an interest tracker visible to admin
 * via /api/admin/wc/registrations.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, BellRing, Check, Sparkles, Smartphone, Monitor } from "lucide-react";

const ACCENTS = {
  tournament: "#FF3B5C",
  immersion:  "#22D3EE",
  "dream-team": "#34D399",
  ctf:        "#60A5FA",
};

const LABELS = {
  tournament:   "Authentic Tournament",
  immersion:    "Immersion League",
  "dream-team": "Dream Team",
  ctf:          "Collect The Flags",
};

const PLAT_LABEL = { mobile: "Mobile", console: "Console" };

export default function WcRegisterBanner({ mode, platform: fixedPlatform = null, dense = false }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // which platform is registering
  const [registered, setRegistered] = useState(false);
  const [regPlatform, setRegPlatform] = useState(null);
  const accent = ACCENTS[mode] || "#FFD700";
  const label = LABELS[mode] || mode;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/wc/register/state");
        if (!cancelled) {
          setRegistered(!!data.modes?.[mode]);
          setRegPlatform(data.platforms?.[mode] || null);
        }
      } catch (e) {
        /* silent — fail-open so the button still appears */
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [mode]);

  const register = async (plat) => {
    if (!plat) { toast.error("Pick a platform first."); return; }
    setBusy(plat);
    try {
      const { data } = await api.post("/wc/register", { mode, platform: plat });
      setRegistered(true);
      setRegPlatform(data.platform || plat);
      if (data.already_registered) {
        toast.message(`You're registered for ${label} · ${PLAT_LABEL[plat] || plat}`);
      } else {
        toast.success(`Thanks for registering for ${label} · ${PLAT_LABEL[plat] || plat}! 🎉`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  if (loading) {
    return (
      <div
        className={`rounded-2xl border bg-black/30 ${dense ? "px-3 py-2" : "px-4 py-3"} flex items-center justify-center`}
        style={{ borderColor: `${accent}33` }}
        data-testid={`wc-register-banner-${mode}-loading`}
      >
        <Loader2 className="w-4 h-4 animate-spin" style={{ color: accent }} />
      </div>
    );
  }

  if (registered) {
    const platTxt = regPlatform ? ` · ${PLAT_LABEL[regPlatform] || regPlatform}` : "";
    return (
      <div
        className={`rounded-2xl border ${dense ? "px-3 py-2.5" : "px-4 py-3.5"} flex items-center gap-3`}
        style={{ borderColor: `${accent}66`, background: `${accent}10` }}
        data-testid={`wc-register-banner-${mode}-confirmed`}
      >
        <div
          className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: `${accent}22`, border: `1px solid ${accent}66` }}
        >
          <Check className="w-4 h-4" style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] font-mono uppercase tracking-[0.25em]"
            style={{ color: accent }}
          >
            // You're in{platTxt}
          </div>
          <div className="text-[13px] text-white/85 font-semibold leading-tight">
            Thanks for registering — we'll notify you the moment {label} unlocks.
          </div>
        </div>
        <Sparkles className="w-4 h-4 shrink-0" style={{ color: accent }} />
      </div>
    );
  }

  // ── Not registered yet ──
  const PlatBtn = ({ plat, Icon }) => (
    <button
      type="button"
      onClick={() => register(plat)}
      disabled={!!busy}
      data-testid={`wc-register-btn-${mode}-${plat}`}
      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-mono uppercase tracking-widest font-bold transition-colors disabled:opacity-60"
      style={{ background: accent, color: "#020617" }}
    >
      {busy === plat ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      {PLAT_LABEL[plat]}
    </button>
  );

  return (
    <div
      className={`rounded-2xl border ${dense ? "px-3 py-2.5" : "px-4 py-3.5"} flex items-center gap-3`}
      style={{ borderColor: `${accent}55`, background: `linear-gradient(135deg, ${accent}10 0%, rgba(2,6,23,0.4) 100%)` }}
      data-testid={`wc-register-banner-${mode}`}
    >
      <div
        className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
        style={{ background: `${accent}1f`, border: `1px solid ${accent}55` }}
      >
        <BellRing className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="text-[10px] font-mono uppercase tracking-[0.25em]"
          style={{ color: accent }}
        >
          // Pre-launch interest
        </div>
        <div className="text-[12.5px] text-white/80 leading-tight">
          {fixedPlatform
            ? <>Reserve your spot for <span className="text-white font-bold">{label}</span> — we'll ping you when it goes live.</>
            : <>Pick your platform to reserve a spot for <span className="text-white font-bold">{label}</span>.</>}
        </div>
      </div>
      {fixedPlatform ? (
        <button
          type="button"
          onClick={() => register(fixedPlatform)}
          disabled={!!busy}
          data-testid={`wc-register-btn-${mode}`}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-mono uppercase tracking-widest font-bold transition-colors disabled:opacity-60"
          style={{ background: accent, color: "#020617" }}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />}
          Register
        </button>
      ) : (
        <div className="shrink-0 flex items-center gap-1.5">
          <PlatBtn plat="mobile" Icon={Smartphone} />
          <PlatBtn plat="console" Icon={Monitor} />
        </div>
      )}
    </div>
  );
}
